import React, { useEffect, useState, useContext, useCallback } from 'react';
import { AppContext } from '../../App';
import { dataFetcher } from '../../Utils/dataFetcher';
import { ComparisonBars } from '../ComparisonBars';
import { RankRow } from './rank-row';
import style from './comparison-lite.module.scss';
import { AthleteSelector } from '../AthleteSelector';
import { CountryFlag } from '../../Utils/flags';
import TagManager from 'react-gtm-module';
import { NoData } from '../NoData';
import { capitalize } from '../../Utils/string';
import { CosmosImage, CosmosText } from '../../cosmos/web-scoped/react';
import athlete_skeleton from '../AthleteProfileLite/athlete_skeleton.png';
import { getAthleteIdById } from '../../Utils/crosslink';

const ComparisonLite = ({ config }) => {
  const [list, setList] = useState();
  const [athletes, setAthletes] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [athleteLeftId, setAthleteLeftId] = useState();
  const [athleteRightId, setAthleteRightId] = useState();
  const [changedLeft, setChangedLeft] = useState(false);
  const [loaded, setLoaded] = useState(true);

  const context = useContext(AppContext);
  const uriConfig = context.config.uriConfig;

  const generateTagManager = useCallback(
    (athletes) => {
      let leftAthlete = {};
      let rightAthlete = {};

      leftAthlete = getAthleteTabObj(
        athletes.find(({ athlete }) => athlete.id === athleteLeftId).athlete
      );
      rightAthlete = getAthleteTabObj(
        athletes.find(({ athlete }) => athlete.id === athleteRightId).athlete
      );

      const dataLayerArgs = {
        dataLayer: {
          event: 'rbVideoTracking',
          eventCategory: 'video',
          eventAction: 'sidebar',
          eventLabel: 'Comparison',
          widgetType: `ComparisonLite - Analysis`,
          widgetTypeProfile: `${leftAthlete.first_name.charAt(0)}.${
            leftAthlete.last_name
          }::${rightAthlete.first_name.charAt(0)}.${rightAthlete.last_name}`
        }
      };

      TagManager.dataLayer(dataLayerArgs);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, changedLeft, athleteRightId, athleteLeftId]);

  useEffect(() => {
    let leftAthlete = {};
    let rightAthlete = {};
    dataFetcher(config.uriList, uriConfig)
      .then((data) => {
        setList(data.entrants);
        leftAthlete = data.entrants[0].athlete;
        rightAthlete = data.entrants[1].athlete;
        setAthleteLeftId(data.entrants[0].athlete.id);
        setAthleteRightId(data.entrants[1].athlete.id);
      })
      .then(() => {
        const dataLayerArgs = {
          dataLayer: {
            event: 'rbVideoTracking',
            eventCategory: 'video',
            eventAction: 'sidebar',
            eventLabel: `loaded`,
            widgetType: `ComparisonLite - Analysis`,
            widgetTypeProfile: `${leftAthlete.first_name.charAt(0)}.${
              leftAthlete.last_name
            }::${rightAthlete.first_name.charAt(0)}.${rightAthlete.last_name}`
          }
        };

        TagManager.dataLayer(dataLayerArgs);
        setLoaded(!loaded);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, uriConfig]);

  useEffect(() => {
    if (athleteLeftId && athleteRightId) {
      dataFetcher(
        config.uriResults
          .replace('{leftId}', athleteLeftId)
          .replace('{rightId}', athleteRightId),
        uriConfig
      )
        .then((data) => {
          if (data.athletes) {
            setAthletes(data.athletes);
            setComparisonData(data.analysis);

            if (!loaded) generateTagManager(data.athletes);
          }
          if (data.error) {
            setComparisonData([]);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteLeftId, athleteRightId, config, uriConfig, generateTagManager]);

  useEffect(() => {
    // initiate module with athlete id
    if (config.hasOwnProperty('initiateWithAthleteId') && list) {
      const athleteId = getAthleteIdById(list, config.initiateWithAthleteId);
      if (athleteId) {
        setAthleteLeftId(athleteId);
        //@todo add undefined state
        setAthleteRightId(-1);
      } else {
        console.warn(`athlete id (${config.initiateWithAthleteId}) not found`);
        setAthleteLeftId(-1);
        setAthleteRightId(-1);
      }
    }
  }, [config, list]);

  const getAthleteTabObj = (athlete) => {
    return {
      id: athlete.id,
      first_name: athlete.first_name,
      last_name: athlete.last_name
    };
  };

  const handleChange = (e) => {
    const leftId = e[0].value;
    const rightId = e[1].value;
    setChangedLeft(leftId !== athleteLeftId);
    setAthleteLeftId(leftId);
    setAthleteRightId(rightId);
  };

  const getMinMax = (value_1, value_2) => {
    if (value_1 > 50 && value_2 > 50) return { min: 50, max: 100, segmentWidth: 5 };
    else if (value_1 < 50 || value_2 < 50) {
      const minNum = Math.min(value_1, value_2);
      const lowerTenth = parseInt(minNum.toString().split('')[0]) * 10; // get lower tenth of number, eg: 47 --> 40, 34 --> 30
      const segment = Math.ceil((100 - lowerTenth) / 10);
      return { min: lowerTenth, max: 100, segmentWidth: segment };
    } else return { min: 0, max: 100, segmentWidth: 10 };
  };

  return (
    <div className={style.comparisonLite}>
      {list && (
        <AthleteSelector
          comparison
          options={list
            .sort((a, b) => {
              return `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` <
                `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
                ? -1
                : `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` >
                  `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
                ? 1
                : 0;
            })
            .map(({ athlete_id, athlete, dynamic, dynamicValue }) => {
              const athleteData = athlete.hasOwnProperty('athlete') ? athlete.athlete : athlete;
              return {
                label: dynamic ? (
                  <>{dynamic}</>
                ) : (
                  <>
                    {(config.showAthleteImage || config.showSelectAthleteImage) && (
                      <CosmosImage
                        class="option-image"
                        image={athleteData.image ? (athleteData.image.profile || athlete_skeleton) : athlete_skeleton}
                        image-alt={`image-${athleteData.first_name}-${athleteData.last_name}`}/>
                    )}
                    <CosmosText
                      class="option-text"
                      kind="subtle">
                      <img
                        className={`flag`}
                        src={CountryFlag(athleteData.nationality.iso3)}
                        alt={athleteData.nationality.iso3}
                      />
                      {`${capitalize(athleteData.first_name)} ${capitalize(athleteData.last_name)}`}
                    </CosmosText>
                  </>
                ),
                descriptionLabel: dynamic ? dynamic : false,
                selectedLabel: (
                  <>
                    <img
                      className={`flag`}
                      src={CountryFlag(athleteData.nationality.iso3)}
                      alt={athleteData.nationality.iso3}
                    />
                    {`${capitalize(athleteData.first_name.substring(0, 1))}. ${capitalize(athleteData.last_name)}`}
                  </>
                ),
                value: dynamicValue ? dynamicValue : athleteData.id,
                rank: dynamic ? 1 : parseInt(athlete_id)
              };
            })}
          onChange={handleChange}
          value={athleteLeftId}
          comparisonValue={athleteRightId}
        />
      )}
      {!comparisonData.length > 0 && <NoData />}

      {comparisonData.length > 0 &&
        comparisonData.map((e) => {
          const values = e.comparison[0].values[0].points;
          const oppValues = e.comparison[0].values[1].points;
          const minMax = getMinMax(values, oppValues);
          return (
            <div key={e.order}>
              <h3 className={style.headline}>{e.header}</h3>
              <ComparisonBars
                min={minMax.min}
                max={minMax.max}
                values={[values]}
                opposite={[oppValues]}
                setRoundName={false}
                segmentWidth={minMax.segmentWidth}
              />
            </div>
          );
        })}

      {athletes.length > 0 && comparisonData.length > 0 && (
        <RankRow
          left={{
            score: athletes[0] ? athletes[0].total_points : undefined,
            rank: athletes[0] ? athletes[0].rank : undefined
          }}
          right={{
            score: athletes[1] ? athletes[1].total_points : undefined,
            rank: athletes[1] ? athletes[1].rank : undefined
          }}
        />
      )}
    </div>
  );
};

export { ComparisonLite };
